// ============================================================
// UYGULAMA AYARLARI — ZORUNLU SÜRÜM + MÜZİK + İLAN SÜRESİ
//
// Mobil uygulamanın en düşük çalışabilir sürümünü buradan
// belirlersiniz. Kullanıcının sürümü bu değerin ALTINDAYSA
// uygulama kapatılamayan bir güncelleme ekranı gösterir.
//
// NEDEN AYRI BİR TABLO YOK:
// Veritabanına yeni tablo/kolon eklemek OData modelinin yeniden
// üretilip yayınlanmasını gerektiriyor. Bu yüzden ayar,
// projede zaten kullanılan "gizli kayıt" yöntemiyle
// MainPage_Category tablosunda tutuluyor (RESMİ TATİL GÜNLERİ
// kaydıyla aynı desen): IsActive=false olduğu için ana sayfada
// kart olarak görünmez. Değerler JSON olarak
// MainPage_Category_Text alanında durur.
// ============================================================

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Button, Card, CardBody, Col, Container, Form,
  FormFeedback, Input, Label, Row, Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

import Breadcrumbs from "components/Common/Breadcrumb";
import { toastSuccess, toastError } from "helpers/toast";
import { ODATA_BASE } from "helpers/api_config";

import SayfaRehberi from "../../../components/Common/SayfaRehberi";
const AYAR_BASLIGI = "UYGULAMA AYARLARI";
// "1.213" / "2.0.1" gibi
const SURUM_KALIBI = /^\d+(\.\d+){0,3}$/;

const UygulamaAyarlari = () => {
  document.title = "Uygulama Ayarları | Berlin Esnaf Admin Paneli";

  const [kayit, setKayit] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const _getir = useCallback(async () => {
    setYukleniyor(true);
    try {
      const res = await axios.get(`${ODATA_BASE}/MainPage_Category`, {
        params: { $filter: `MainPage_Category_Title eq '${AYAR_BASLIGI}'`, $top: 1 },
      });
      const bulunan = ((res.data && res.data.value) || [])[0] || null;
      setKayit(bulunan);
    } catch (e) {
      toastError("Ayarlar okunamadı.");
      setKayit(null);
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { _getir(); }, [_getir]);

  const mevcutAyar = (() => {
    if (!kayit || !kayit.MainPage_Category_Text) return {};
    try {
      const c = JSON.parse(kayit.MainPage_Category_Text);
      return c && typeof c === "object" ? c : {};
    } catch (e) {
      return {};
    }
  })();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      minAndroid: mevcutAyar.minAndroid || "",
      minIOS: mevcutAyar.minIOS || "",
      zorunluMesaj: mevcutAyar.zorunluMesaj || "",
      // Anahtar yoksa bölüm açık sayılır (mobil muzikGorunurluk.js ile aynı kural)
      muzikAcik: mevcutAyar.muzikAcik !== false,
      // 0 / boş = süre sınırı yok (hiçbir ilan düşmez)
      ilanYayinSuresiGun: mevcutAyar.ilanYayinSuresiGun != null ? String(mevcutAyar.ilanYayinSuresiGun) : "",
    },
    validationSchema: Yup.object({
      minAndroid: Yup.string().trim().matches(SURUM_KALIBI, "Sürüm 1.213 biçiminde olmalı").required("Zorunlu"),
      minIOS: Yup.string().trim().matches(SURUM_KALIBI, "Sürüm 1.213 biçiminde olmalı").required("Zorunlu"),
      zorunluMesaj: Yup.string().trim().max(300, "En fazla 300 karakter"),
      ilanYayinSuresiGun: Yup.string().trim()
        .matches(/^\d{0,4}$/, "Yalnızca gün sayısı yazın")
        .test("makul", "1-3650 arası bir gün sayısı girin", v => {
          if (!v || v.trim() === "" || v.trim() === "0") return true;
          const n = Number(v);
          return n >= 1 && n <= 3650;
        }),
    }),
    onSubmit: async values => {
      if (!kayit) {
        toastError("Ayar kaydı bulunamadı; önce kaydı oluşturun.");
        return;
      }
      setKaydediliyor(true);
      try {
        // Mevcut JSON ile BIRLESTIRILIR: eskiden yalnizca bu formdaki
        // alanlar yaziliyordu; kayda baska bir anahtar eklenirse (magaza
        // adresleri vb.) ilk kaydette sessizce siliniyordu.
        const govde = {
          MainPage_Category_Text: JSON.stringify({
            ...mevcutAyar,
            minAndroid: values.minAndroid.trim(),
            minIOS: values.minIOS.trim(),
            zorunluMesaj: values.zorunluMesaj.trim(),
            muzikAcik: !!values.muzikAcik,
            ilanYayinSuresiGun: Number(values.ilanYayinSuresiGun) || 0,
          }),
        };
        await axios.patch(`${ODATA_BASE}/MainPage_Category(${kayit.ID})`, govde);
        toastSuccess("Ayarlar kaydedildi.");
        _getir();
      } catch (e) {
        toastError("Ayarlar kaydedilemedi.");
      } finally {
        setKaydediliyor(false);
      }
    },
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Uygulama Ayarları" />
          <SayfaRehberi sayfa="uygulamaayarlari" />

          {yukleniyor ? (
            <Card><CardBody className="text-center py-5"><Spinner /></CardBody></Card>
          ) : !kayit ? (
            <Card>
              <CardBody>
                <h5 className="text-danger">Ayar kaydı bulunamadı</h5>
                <p className="mb-0">
                  <code>MainPage_Category</code> tablosunda başlığı{" "}
                  <code>{AYAR_BASLIGI}</code> olan, <code>IsActive = 0</code> bir kayıt olmalı.
                  Kayıt yokken mobil uygulama zorunlu güncelleme uygulamaz (uygulama
                  erişilemez hâle gelmez).
                </p>
              </CardBody>
            </Card>
          ) : (
            <Row>
              <Col lg="8">
                <Card>
                  <CardBody>
                    <h5 className="mb-1">Zorunlu Güncelleme</h5>
                    <p className="text-muted">
                      Kullanıcının sürümü aşağıdaki değerin <strong>altındaysa</strong> uygulama
                      kapatılamayan bir güncelleme ekranı gösterir ve kullanılamaz.
                      Mağazadaki sürümle <strong>aynı</strong> değeri girerseniz kimse
                      engellenmez; yalnızca daha yeni sürüm varsa atlanabilir uyarı çıkar.
                    </p>

                    <Alert color="warning" className="mb-4">
                      <strong>Dikkat:</strong> Buraya mağazada <em>henüz yayında olmayan</em> bir
                      sürüm yazarsanız, tüm kullanıcılar güncelleyemeyecekleri bir ekranda
                      kilitli kalır. Önce yeni sürümün Google Play / App Store’da
                      yayınlandığından emin olun.
                    </Alert>

                    <Form onSubmit={e => { e.preventDefault(); validation.handleSubmit(); return false; }}>
                      <Row>
                        <Col md="6">
                          <div className="mb-3">
                            <Label className="form-label">Android — en düşük sürüm</Label>
                            <Input
                              name="minAndroid"
                              type="text"
                              placeholder="1.213"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.minAndroid}
                              invalid={!!(validation.touched.minAndroid && validation.errors.minAndroid)}
                            />
                            {validation.touched.minAndroid && validation.errors.minAndroid ? (
                              <FormFeedback type="invalid">{validation.errors.minAndroid}</FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                        <Col md="6">
                          <div className="mb-3">
                            <Label className="form-label">iOS — en düşük sürüm</Label>
                            <Input
                              name="minIOS"
                              type="text"
                              placeholder="1.213"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.minIOS}
                              invalid={!!(validation.touched.minIOS && validation.errors.minIOS)}
                            />
                            {validation.touched.minIOS && validation.errors.minIOS ? (
                              <FormFeedback type="invalid">{validation.errors.minIOS}</FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>

                      <div className="mb-3">
                        <Label className="form-label">Kullanıcıya gösterilecek mesaj</Label>
                        <Input
                          name="zorunluMesaj"
                          type="textarea"
                          rows="3"
                          placeholder="Uygulamanın bu sürümü artık desteklenmiyor..."
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.zorunluMesaj}
                          invalid={!!(validation.touched.zorunluMesaj && validation.errors.zorunluMesaj)}
                        />
                        {validation.touched.zorunluMesaj && validation.errors.zorunluMesaj ? (
                          <FormFeedback type="invalid">{validation.errors.zorunluMesaj}</FormFeedback>
                        ) : null}
                      </div>

                      <hr className="my-4" />
                      <h5 className="mb-1">İlan Yayın Süresi</h5>
                      <p className="text-muted">
                        Bu süreden eski ilanlar uygulamadaki ilan listesinde
                        <strong> gösterilmez</strong>. Kayıtlar silinmez: sahibi
                        “İlanlarım”da görmeye devam eder, yönetici hepsini görür.
                        Süresi devam eden <strong>öne çıkan (ücretli)</strong> ilanlar
                        eskiyse de düşmez. <strong>Boş veya 0 = süre sınırı yok.</strong>
                      </p>
                      <Row>
                        <Col md="6">
                          <div className="mb-4">
                            <Label className="form-label">İlan yayın süresi (gün)</Label>
                            <Input
                              name="ilanYayinSuresiGun"
                              type="text"
                              inputMode="numeric"
                              placeholder="90"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.ilanYayinSuresiGun}
                              invalid={!!(validation.touched.ilanYayinSuresiGun && validation.errors.ilanYayinSuresiGun)}
                            />
                            {validation.touched.ilanYayinSuresiGun && validation.errors.ilanYayinSuresiGun ? (
                              <FormFeedback type="invalid">{validation.errors.ilanYayinSuresiGun}</FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>

                      <hr className="my-4" />
                      <h5 className="mb-1">Müzik Bölümü</h5>
                      <p className="text-muted">
                        Şarkı dosyaları sunucuda yokken bölüm açık kalırsa kullanıcı
                        çalmayan bir liste görür. Kapalıyken ana sayfadaki “Müzik” kartı ve
                        yan menüdeki bağlantı gizlenir. (iOS’ta bölüm zaten hiç gösterilmiyor.)
                      </p>
                      <div className="form-check form-switch form-switch-lg mb-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="ayarMuzikAcik"
                          name="muzikAcik"
                          checked={!!validation.values.muzikAcik}
                          onChange={e => validation.setFieldValue("muzikAcik", e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="ayarMuzikAcik">
                          {validation.values.muzikAcik ? "Müzik bölümü açık" : "Müzik bölümü kapalı"}
                        </label>
                      </div>

                      <div className="text-end">
                        <Button color="success" type="submit" disabled={kaydediliyor}>
                          {kaydediliyor ? "Kaydediliyor..." : "Kaydet"}
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>

              <Col lg="4">
                <Card>
                  <CardBody>
                    <h6 className="mb-3">Nasıl çalışır?</h6>
                    <ul className="ps-3 mb-0 text-muted" style={{ lineHeight: 1.9 }}>
                      <li>Uygulama her açılışta bu ayarı okur.</li>
                      <li>Sürüm <strong>düşükse</strong> → kapatılamayan ekran.</li>
                      <li>Sürüm yeterli ama mağazada daha yenisi varsa → “Daha Sonra” denilebilen uyarı.</li>
                      <li>Ayar okunamazsa (internet yok / kayıt silinmiş) zorunlu güncelleme <strong>uygulanmaz</strong>.</li>
                      <li>Müzik anahtarı yalnızca uygulamanın 11 Eylül 2026 sonrası yayınlanan sürümlerinde etkilidir; eski sürümler bölümü göstermeye devam eder.</li>
                    </ul>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UygulamaAyarlari;
